import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getClaims, STATUS_BADGE_CLASS } from "./claimData";
import "./claim.css";

/* My Claims — ported from the prototype (claim.html).
   Compact summary cards (icon left, amount right), one-row filters, and
   per-status row actions: View / Edit while editable, Delete only on Draft. */

const CLAIM_TYPES = [
  "All Claim Types",
  "Cash Claim",
  "Credit Card Claim",
  "Local Travel Claim",
  "Overseas Travel Claim",
  "Meal Allowance",
  "Mileage Claim",
];

const STATUSES = ["All Statuses", "Draft", "Pending Approval", "Approved", "Paid", "Returned"];

const canEdit = (status) => status === "Pending Approval" || status === "Draft" || status === "Returned";
const canView = (status) => status !== "Draft";

const ClaimListPage = () => {
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("All Claim Types");
  const [statusFilter, setStatusFilter] = useState("All Statuses");
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deletedIds, setDeletedIds] = useState(() => new Set());

  const rows = useMemo(() => {
    const query = search.trim().toLowerCase();
    return getClaims().filter((row) => {
      if (deletedIds.has(row.id)) return false;
      if (typeFilter !== "All Claim Types" && row.type !== typeFilter) return false;
      if (statusFilter !== "All Statuses" && row.status !== statusFilter) return false;
      if (!query) return true;
      return [row.id, row.type, row.title, row.desc, row.code].some((value) =>
        value.toLowerCase().includes(query)
      );
    });
  }, [search, typeFilter, statusFilter, deletedIds]);

  const closeDeleteModal = () => setDeleteTarget(null);

  const confirmDelete = () => {
    if (!deleteTarget) return;
    setDeletedIds((prev) => {
      const next = new Set(prev);
      next.add(deleteTarget);
      return next;
    });
    closeDeleteModal();
  };

  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key === "Escape") closeDeleteModal();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, []);

  const resetFilters = () => {
    setSearch("");
    setTypeFilter("All Claim Types");
    setStatusFilter("All Statuses");
  };

  return (
    <div className="bes-claim">
      <div className="claim-page-head">
        <div className="title-left">
          <div className="page-icon claim-icon">
            <i className="ri-file-list-3-line" />
          </div>
          <div>
            <h1>My Claims</h1>
            <p>Create, submit and track your expense claims, travel claims and allowance claims.</p>
          </div>
        </div>
        <button type="button" className="btn btn-primary" onClick={() => navigate("/claim/create")}>
          <i className="ri-add-line" /> New Claim
        </button>
      </div>

      <div className="summary-cards">
        <div className="summary-card">
          <div className="summary-icon icon-blue">
            <i className="ri-file-list-2-line" />
          </div>
          <div>
            <h3>Total Claims</h3>
            <strong>18</strong>
          </div>
        </div>
        <div className="summary-card">
          <div className="summary-icon icon-amber">
            <i className="ri-time-line" />
          </div>
          <div>
            <h3>Pending Approval</h3>
            <strong>4</strong>
          </div>
        </div>
        <div className="summary-card">
          <div className="summary-icon icon-indigo">
            <i className="ri-wallet-3-line" />
          </div>
          <div>
            <h3>Approved / Paid</h3>
            <strong>11</strong>
          </div>
        </div>
        <div className="summary-card">
          <div className="summary-icon icon-green">
            <i className="ri-coins-line" />
          </div>
          <div>
            <h3>Total Claimed</h3>
            <strong>RM 18,924</strong>
          </div>
        </div>
      </div>

      <div className="data-container">
        <div className="filters-standard">
          <div className="filter-group search">
            <i className="ri-search-line" />
            <input
              className="form-control"
              type="text"
              placeholder="Search by claim ID, type, description or project code..."
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
          </div>
          <div className="filter-group type-filter">
            <select
              className="form-control"
              aria-label="Claim Type"
              value={typeFilter}
              onChange={(event) => setTypeFilter(event.target.value)}
            >
              {CLAIM_TYPES.map((type) => (
                <option key={type}>{type}</option>
              ))}
            </select>
          </div>
          <div className="filter-group status-filter">
            <select
              className="form-control"
              aria-label="Status"
              value={statusFilter}
              onChange={(event) => setStatusFilter(event.target.value)}
            >
              {STATUSES.map((status) => (
                <option key={status}>{status}</option>
              ))}
            </select>
          </div>
          <button type="button" className="btn btn-outline" onClick={resetFilters}>
            <i className="ri-refresh-line" /> Reset
          </button>
        </div>

        <div className="table-responsive">
          <table className="data-table">
            <thead>
              <tr>
                <th>Claim ID</th>
                <th>Claim Type</th>
                <th>Description</th>
                <th>Period / Date</th>
                <th>Project / Budget Code</th>
                <th>Amount (RM)</th>
                <th>Status</th>
                <th className="text-center">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.id}>
                  <td className="font-bold">#{row.id}</td>
                  <td>{row.type}</td>
                  <td>
                    <div className="claim-title">{row.title}</div>
                    <div className="claim-desc">{row.desc}</div>
                  </td>
                  <td>{row.period}</td>
                  <td>{row.code}</td>
                  <td className="font-bold">{row.amount}</td>
                  <td>
                    <span className={`status-badge ${STATUS_BADGE_CLASS[row.status] || "claim-status-draft"}`}>
                      {row.status}
                    </span>
                  </td>
                  <td>
                    <div className="actions-cell">
                      {canView(row.status) && (
                        <button
                          type="button"
                          className="action-btn"
                          title="View"
                          onClick={() => navigate(`/claim/${row.id}`)}
                        >
                          <i className="ri-eye-line" />
                        </button>
                      )}
                      {canEdit(row.status) && (
                        <button
                          type="button"
                          className="action-btn"
                          title="Edit"
                          onClick={() => navigate("/claim/create")}
                        >
                          <i className="ri-edit-line" />
                        </button>
                      )}
                      {row.status === "Draft" && (
                        <button
                          type="button"
                          className="action-btn delete-btn"
                          title="Delete"
                          onClick={() => setDeleteTarget(row.id)}
                        >
                          <i className="ri-delete-bin-line" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
              {rows.length === 0 && (
                <tr>
                  <td colSpan={8} className="text-muted empty-row">
                    No claims match the current filters.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="pagination-container">
          <span className="showing-text text-muted">
            Showing {rows.length ? 1 : 0} to {rows.length} of {rows.length} entries
          </span>
          <div className="pagination">
            <button type="button" className="page-btn" disabled>
              <i className="ri-arrow-left-double-line" />
            </button>
            <button type="button" className="page-btn" disabled>
              <i className="ri-arrow-left-s-line" />
            </button>
            <button type="button" className="page-btn active">
              1
            </button>
            <button type="button" className="page-btn" disabled>
              <i className="ri-arrow-right-s-line" />
            </button>
            <button type="button" className="page-btn" disabled>
              <i className="ri-arrow-right-double-line" />
            </button>
          </div>
        </div>
      </div>

      {deleteTarget && (
        <div
          className="confirm-modal-overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="deleteClaimTitle"
          onClick={(event) => {
            if (event.target === event.currentTarget) closeDeleteModal();
          }}
        >
          <div className="confirm-modal-box">
            <div className="confirm-modal-header">
              <h3 id="deleteClaimTitle">Delete Claim</h3>
            </div>
            <div className="confirm-modal-body">
              <p>Are you sure you want to delete this claim? This action cannot be undone.</p>
              <p className="confirm-target-label">Claim ID: #{deleteTarget}</p>
            </div>
            <div className="confirm-modal-actions">
              <button type="button" className="btn btn-outline" onClick={closeDeleteModal}>
                No, Keep Claim
              </button>
              <button type="button" className="btn btn-primary" onClick={confirmDelete}>
                Yes, Delete Claim
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClaimListPage;
